'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

function findScrollableAncestor(element: HTMLElement | null): HTMLElement | null {
  let current = element?.parentElement ?? null;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

interface UsePullToRefreshOptions {
  /** Minimum pull distance to trigger refresh (default: 80px) */
  threshold?: number;
  /** Maximum visual pull distance (default: 128px) */
  maxPull?: number;
  /** Resistance factor for exponential decay (default: 0.4) */
  resistance?: number;
}

interface UsePullToRefreshReturn {
  /** Current pull distance (0 when not pulling) */
  pullDistance: number;
  /** Whether refresh is in progress */
  isRefreshing: boolean;
  /** Ref to attach to the scrollable container */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * Hook for implementing pull-to-refresh gesture on mobile.
 *
 * Based on best practices from:
 * - https://www.strictmode.io/articles/react-pull-to-refresh
 * - https://blog.logrocket.com/implementing-pull-to-refresh-react-tailwind-css/
 *
 * @param onRefresh - Async callback to execute on refresh
 * @param options - Configuration options
 * @returns Pull state and container ref
 *
 * @example
 * ```tsx
 * const { refetch } = useQuery({...});
 * const { pullDistance, isRefreshing, containerRef } = usePullToRefresh(
 *   () => refetch()
 * );
 *
 * return (
 *   <div ref={containerRef}>
 *     {pullDistance > 0 && <RefreshIndicator distance={pullDistance} />}
 *     <Content />
 *   </div>
 * );
 * ```
 */
export function usePullToRefresh(
  onRefresh: () => Promise<unknown>,
  options: UsePullToRefreshOptions = {},
): UsePullToRefreshReturn {
  const { threshold = 80, maxPull = 128, resistance = 0.4 } = options;

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const startY = useRef(0);
  const isPulling = useRef(false);
  const pullDistanceRef = useRef(0);
  const isRefreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  const scrollParentRef = useRef<HTMLElement | null>(null);

  onRefreshRef.current = onRefresh;

  const applyResistance = useCallback(
    (distance: number): number => {
      return maxPull * (1 - Math.exp((-resistance * distance) / maxPull));
    },
    [maxPull, resistance],
  );

  const isAtTop = useCallback((): boolean => {
    const scrollParent = scrollParentRef.current;
    if (scrollParent) return scrollParent.scrollTop === 0;
    const container = containerRef.current;
    if (!container) return window.scrollY === 0;
    return container.scrollTop === 0;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    scrollParentRef.current = findScrollableAncestor(container);
    const target = container || document.body;

    const handleTouchStart = (e: TouchEvent) => {
      if (!isAtTop()) return;

      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling.current || isRefreshingRef.current) return;

      const currentY = e.touches[0].clientY;
      const diff = currentY - startY.current;

      if (diff < 0) {
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      if (!isAtTop()) {
        isPulling.current = false;
        pullDistanceRef.current = 0;
        setPullDistance(0);
        return;
      }

      const resistedDistance = applyResistance(diff);
      pullDistanceRef.current = resistedDistance;
      setPullDistance(resistedDistance);

      if (diff > 0) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = async () => {
      if (!isPulling.current) return;

      isPulling.current = false;

      if (pullDistanceRef.current >= threshold && !isRefreshingRef.current) {
        isRefreshingRef.current = true;
        setIsRefreshing(true);
        setPullDistance(threshold);

        try {
          await onRefreshRef.current();
        } catch (error) {
          console.error('Pull to refresh failed:', error);
        } finally {
          isRefreshingRef.current = false;
          setIsRefreshing(false);
          pullDistanceRef.current = 0;
          setPullDistance(0);
        }
      } else {
        pullDistanceRef.current = 0;
        setPullDistance(0);
      }
    };

    target.addEventListener('touchstart', handleTouchStart, { passive: true });
    target.addEventListener('touchmove', handleTouchMove, { passive: false });
    target.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      target.removeEventListener('touchstart', handleTouchStart);
      target.removeEventListener('touchmove', handleTouchMove);
      target.removeEventListener('touchend', handleTouchEnd);
    };
  }, [applyResistance, isAtTop, threshold]);

  return {
    pullDistance,
    isRefreshing,
    containerRef,
  };
}
